import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Tippy from '@tippyjs/react';
import dayjs from 'dayjs';

import { selectUserActions } from 'src/reducers/contentful.reducer';
import { setCurrentEntity, setLoading, setModal, setShareModal } from 'src/reducers/app.reducer';
import {
  updateActionPlan,
  createCustomAction,
  selectUser,
  selectSelectedAccount,
  selectActiveAccount,
  updateActionSubContractorInfo,
  selectImpersonated,
  fetchEntityActions,
  selectEntityActions,
  clearEntityActions,
} from 'src/reducers/account.reducer';

import { CgAttachment } from 'react-icons/cg';
import { RiShareForwardLine, RiDeleteBin5Line } from 'react-icons/ri';
import { PiXCircleBold } from 'react-icons/pi';
import { AiOutlineFileAdd, AiOutlineFileDone, AiOutlineFileExcel, AiOutlineFilePdf, AiOutlineFileWord } from 'react-icons/ai';
import { BiDuplicate } from 'react-icons/bi';

import ActionsHeader from './Header';
import { Modal } from 'src/components/modal/Modal';
import { Button } from 'src/components/Button';
import { PlaceholderLine, PlaceholderStatus } from './PlaceHolder';
import ActionStatus from './ActionStatus';
import { FileExplorer } from '../../file-explorer/FileExplorer';

import {
  compareByUrgency,
  compareQueryToActionName,
  getActionDetail,
  getActionProductName,
  getAuthorizedProducts,
  getFileNameFromKey,
  getResponsibleName,
  getVersionIdByName,
  getVersionName,
  isEmailValid,
  retrieveSurveysResponses,
  stringAvatar,
  verifyResponsesMatchConditions,
} from 'src/utils/utils';
import { ActionProps, EntityProps } from '../types';
import { actionsUrgencyComplexity, category, urgencyComplexity } from '../constants';
import { MODAL_TYPES } from '../../../utils/constants';
import { getSignedUrlByKey } from '../../../utils/api';
import { InputComponent } from 'src/components/form/InputComponent';
import { MenuItem, Avatar, Checkbox, IconButton, TextField } from '@mui/material';

import {
  DataTableContainer,
  RowContainer,
  Div,
  CustomChip,
  AvatarContainer,
  CustomStack,
  Infos,
  NoDataContainer,
  AdminRowContainer,
  LinkField,
  ErasePlaceholder,
  HeaderActions,
  ActionContainer,
  ModalContent,
  AdjustActions,
  ButtonsContainer,
  BasicModalContainer,
  ButtonContainer,
  ChatBubbleIcon,
} from './Table.styles';
import ActionDetails from './ActionDetails';
import { Divider } from '@mui/material';
import useDidUpdate from 'src/hooks/useDidUpdate';

interface TableProps {
  admin?: boolean;
  checkedActions?: string[];
  entities?: string[];
  setCheckedActions?: (prevState: any) => void;
  data?: [
    {
      [key: string]: any;
    },
  ];
  view?: string;
}

const Table = ({ admin = false, view }: TableProps) => {
  const dispatch = useDispatch<any>();
  const impersonated = useSelector(selectImpersonated);
  const currentUser = useSelector(selectUser);
  const selectedAccount = useSelector(selectSelectedAccount);
  const activeAccount = useSelector(selectActiveAccount);
  const userActions = useSelector(selectUserActions);
  const entityActions = useSelector(selectEntityActions);
  const [query, setQuery] = useState<string>('');

  const [selectedAction, setSelectedAction] = useState<ActionProps>({});
  const [editedAction, setEditedAction] = useState<ActionProps>({});

  const [selectedEntity, setSelectedEntity] = useState<EntityProps>(null);
  const [selectAllActions, setSelectAllActions] = useState<boolean>(false);

  const customActionInitialState = {
    title: '',
    description: '',
    complexity: '',
    urgency: '',
    category: '',
    explanation: '',
    precision: '',
    conditions: [],
    custom: true,
    isReady: false,
  };
  const [customAction, setCustomAction] = useState<{
    [key: string]: string | boolean | { [key: string]: string }[] | [];
    conditions: { [key: string]: string }[] | [];
    isReady: boolean;
  }>(customActionInitialState);

  const [toDeleteActions, setToDeleteActions] = useState<Array<ActionProps>>([]);

  const [filters, setFilters] = useState({ urgency: [], status: [], category: [], product: [], complexity: [], archived: false });

  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [showCustomActionModal, setShowCustomActionModal] = useState<boolean>(false);

  const [showAvailableDocuments, setShowAvailableDocuments] = useState({ action: undefined, show: false, context: undefined });

  const attributeRef = useRef<HTMLDivElement>(null);
  const documentRef = useRef<HTMLDivElement>(null);

  const [showSubcontractorInputModal, setShowSubcontractorInputModal] = useState<boolean>(false);
  const [subContractorContact, setSubContractorContact] = useState<string>('');
  const [selectedDrawerItemID, setSelectedDrawerItemID] = useState<string | null>(null);

  const [adminFiltredActions, setAdminFiltredActions] = useState<Array<ActionProps>>([]);
  const [checkedActions, setCheckedActions] = useState<Array<ActionProps>>([]);
  //
  // For Admin Table : Create Actions array with the combo of DB & Contentfull
  //

  const adminData = () => {
    const { versSurveysAnswers, nonVersSurveysAnswers } = retrieveSurveysResponses(selectedAccount, selectedEntity);
    const entityLevelActions = [];
    const contentActions = userActions?.reduce((result, action) => {
      if (versSurveysAnswers.length > 0) {
        versSurveysAnswers.forEach(elt => {
          const { isMatchConditions, isEntityScoop } = verifyResponsesMatchConditions(action.conditions, elt.responses, nonVersSurveysAnswers);
          if (isMatchConditions && isEntityScoop) {
            const exists = entityLevelActions.some(item => item.id === action.id);
            if (!exists) {
              entityLevelActions.push({ ...action, isEntityScoop, entityId: selectedEntity._id });
            }
          }
          if (elt.responses && isMatchConditions && !isEntityScoop) {
            const data = {
              ...elt,
            };
            if (action.category === 'Audit') {
              const conditions = action?.conditions?.filter(elt => elt.value === 'other')?.map(elt => `${elt.key}_${elt.value}`);
              conditions?.forEach(condition => {
                if (data?.responses[condition] && Array.isArray(data?.responses[condition])) {
                  data.responses[condition].forEach(element => {
                    result.push({
                      ...action,
                      ...data,
                      subContractor: { name: element },
                      responses: undefined,
                    });
                  });
                }
              });
            } else {
              delete data['responses'];
              result.push({
                ...action,
                ...data,
              });
            }
          }
        });
      } else if (nonVersSurveysAnswers) {
        const { isMatchConditions, isEntityScoop } = verifyResponsesMatchConditions(action.conditions, null, nonVersSurveysAnswers);
        if (isMatchConditions && isEntityScoop) {
          const exists = entityLevelActions.some(item => item.id === action.id);
          if (!exists) {
            entityLevelActions.push({ ...action, isEntityScoop, entityId: selectedEntity._id });
          }
        }
      }

      return result;
    }, []);
    // add also the entity level actions
    contentActions.push(...entityLevelActions);
    const mergedActions = entityActions?.reduce((result, dbAction) => {
      const correspondingContentAction = contentActions.find(contentAction => {
        if (!contentAction.isEntityScoop) {
          return (
            contentAction.id === dbAction?.id &&
            contentAction.relatedProductId === dbAction.relatedProductId &&
            contentAction.versionProductId === dbAction.versionProductId
          );
        } else {
          return dbAction.id === contentAction.id;
        }
      });

      if (correspondingContentAction) {
        // If there is a corresponding action in contentActions, merge the data
        result.push({
          ...correspondingContentAction,
          ...dbAction,
        });
      } else {
        // If there is no corresponding action in contentActions, use the data from actionsDB
        result.push({ ...dbAction, productName: getActionProductName(dbAction.relatedProductId) });
      }

      return result;
    }, []);

    // Add any remaining actions from contentActions that were not found in actionsDB
    const remainingActions = contentActions?.filter(
      contentAction =>
        !entityActions?.some(dbAction => {
          if (!dbAction.isEntityScoop) {
            if (contentAction.category === 'Audit') {
              return (
                contentAction.id === dbAction?.id &&
                contentAction.relatedProductId === dbAction.relatedProductId &&
                contentAction.versionProductId === dbAction.versionProductId &&
                contentAction.subContractor.name === dbAction.subContractor.name
              );
            }
            return (
              dbAction.id === contentAction.id &&
              dbAction.relatedProductId === contentAction.relatedProductId &&
              dbAction.versionProductId === contentAction.versionProductId
            );
          } else {
            return dbAction.id === contentAction.id;
          }
        })
    );
    mergedActions?.push(...remainingActions);

    return filteredData(mergedActions);
  };

  const filterAdminActions = () => {
    let filtredActions;
    if (selectedEntity) {
      const adminActions = adminData();
      if (adminActions) {
        if (view === 'backlog') {
          filtredActions = adminActions.filter(action => !action.draft && !action.active);
        } else if (view === 'actionsPrepared') {
          filtredActions = adminActions.filter(action => action.draft && !action.active);
        } else {
          filtredActions = adminActions.filter(action => action.active === true);
        }
      }
      setCheckedActions([]);
      setAdminFiltredActions([...filtredActions]);
    }
  };

  useEffect(() => {
    if (admin) {
      filterAdminActions();
      setSelectAllActions(false);
    }
  }, [admin, view, selectedEntity, activeAccount, entityActions, filters, query]);

  useEffect(() => {
    if (selectAllActions) {
      adminFiltredActions.forEach(action => {
        handleSelectActions(action);
      });
    } else {
      setCheckedActions([]);
    }
  }, [selectAllActions]);

  const [isDataLoading, setIsDataLoading] = useState(false);

  useDidUpdate(async () => {
    if (!isDataLoading) setIsDataLoading(true);
    if (entityActions) dispatch(clearEntityActions());
    if (selectedEntity?._id) {
      let data = { accountId: null, entityId: selectedEntity?._id };
      if (impersonated?._id) {
        data.accountId = impersonated?._id;
      } else if (selectedAccount?._id && admin) {
        data.accountId = selectedAccount?._id;
      } else if (activeAccount?._id) {
        data.accountId = activeAccount?._id;
      }
      await dispatch(fetchEntityActions(data));
      setIsDataLoading(false);
    }
  }, [selectedEntity?._id]);

  const clientSideActionsDataCount = useMemo(() => {
    return entityActions?.filter((item: ActionProps) => {
      switch (view) {
        case 'actions':
          return !['Demande', 'Audit'].includes(item.category);
        case 'requests':
          return item.category === 'Demande';
        case 'audits':
          return item.category === 'Audit';
        default:
          return true; // Include all items if view is not recognized
      }
    })?.length;
  }, [entityActions, view]);

  // Handle Display Drawer in case the url contains the action info
  // @ts-ignore
  const { id } = useParams();

  const actionId = id?.split('|')[0];
  const relatedProductId = id?.split('|')[1];
  const entityId = id?.split('|')[2];
  const versionProductId = id?.split('|')[3];
  const selectedDrawerID = id?.split('|')[4];

  useEffect(() => {
    if (actionId && relatedProductId && entityId && versionProductId) {
      const actionDetail = getActionDetail(entityId, actionId, relatedProductId, versionProductId);
      const entity = activeAccount?.entities?.find(entity => entity._id === entityId);
      if (selectedDrawerID !== 'chat') {
        setSelectedDrawerItemID('detail');
      } else {
        setSelectedDrawerItemID(selectedDrawerID);
      }
      if (actionDetail.active && entity) {
        setSelectedEntity(entity);
        setSelectedAction(actionDetail);
        setEditedAction(actionDetail);
        setShowDrawer(true);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedAction.category === 'Audit') {
      if (selectedAction?.subContractor?.contactEmail) {
        setShowSubcontractorInputModal(false);
      } else {
        setShowSubcontractorInputModal(true);
      }
    }
  }, [selectedAction]);

  //
  // Loading state
  //
  const loadingTable = () => {
    return (
      <>
        {/* <DataTableContainer>
          <div className='container-fluid data-table font-weight-bold'>
            <div className='row'>
              {admin && (
                <Div className='col-0-5' bolder center>
                  {filters.archived ? '' : 'Valider'}
                </Div>
              )}
              <Div className={admin ? 'col-1-5' : 'col-2'} bolder>
                <span>Action</span>
              </Div>
              <Div className='col-1' bolder>
                Produit / Entité
              </Div>
              <Div className='col-1' center bolder>
                Statut
              </Div>
              <Div className='col-1' center bolder>
                Date d'échéance
              </Div>
              <Div className='col-1' center bolder>
                Criticité
              </Div>
              <Div className={admin ? 'col-1' : 'col-1'} center bolder>
                Complexité
              </Div>
              <Div className={admin ? 'col-1' : 'col-1'} center bolder>
                Catégorie
              </Div>
              <Div className={admin ? 'col-1' : 'col-1-5'} bolder>
                {!admin ? 'Responsable de la tâche' : 'Responsable'}
              </Div>
              {!admin && <Div className='col-1' bolder></Div>}
              {admin && (
                <>
                  <Div className='col-1' bolder>
                    Document
                  </Div>
                  <Div className='col-0-5' bolder>
                    Dupliquer
                  </Div>
                </>
              )}
            </div>
          </div>
          <Divider />
        </DataTableContainer> */}
        <DataTableContainer>
          <div className='container-fluid data-table'>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i}>
                <AdminRowContainer className='row'>
                  {admin && <Div className='col-0-5' bolder center></Div>}
                  {/* Action Title */}
                  <Div className={admin ? 'col-1-5' : 'col-2'} bolder>
                    <PlaceholderLine width={90} />
                  </Div>
                  {/* Product Title */}
                  <Div className='col-1' bolder>
                    <PlaceholderLine width={90} />
                  </Div>
                  {/* Status */}
                  <Div className='col-1' center bolder>
                    <PlaceholderLine width={60} />
                  </Div>
                  {/* Date d'échéance  */}
                  <Div className='col-1' center bolder>
                    <PlaceholderLine width={80} />
                  </Div>
                  {/* Urgency */}
                  <Div className='col-1' center bolder>
                    <PlaceholderLine width={80} />
                  </Div>
                  {/* Complexity */}
                  <Div className='col-1' center bolder>
                    <PlaceholderLine width={80} />
                  </Div>
                  {/* Category */}
                  <Div className='col-1' center bolder>
                    <PlaceholderLine width={80} />
                  </Div>
                  {/* Task Responsable */}
                  <Div className={admin ? 'col-1' : 'col-1-5'} bolder>
                    <PlaceholderStatus width={80} />
                  </Div>
                  {/* Actions */}
                  {admin ? (
                    <>
                      <Div className='col-1' bolder center>
                        <PlaceholderLine width={40} />
                      </Div>
                      <Div className='col-0-5' bolder></Div>
                    </>
                  ) : (
                    <Div className='col-1' bolder>
                      <PlaceholderLine width={20} />
                    </Div>
                  )}
                </AdminRowContainer>
                {i < 4 && <Divider light />}
              </div>
            ))}
          </div>
        </DataTableContainer>
      </>
    );
  };

  //
  // Action handlers
  //

  const handleCheckedActions = (event, action, type?, isEntityScoop: boolean = false) => {
    const { id, relatedProductId, versionProductId, subContractor } = action;

    const updatedAdminFiltredActions = [...adminFiltredActions];
    const itemIndex = updatedAdminFiltredActions.findIndex(
      isEntityScoop
        ? item => item.id === id && item.versionProductId === selectedEntity?._id
        : action.category === 'Audit'
          ? item =>
              item.id === id &&
              item.relatedProductId === relatedProductId &&
              item.versionProductId === versionProductId &&
              item.subContractor.name === subContractor.name
          : item => item.id === id && item.relatedProductId === relatedProductId && item.versionProductId === versionProductId
    );

    if (itemIndex !== -1) {
      updatedAdminFiltredActions[itemIndex] = {
        ...updatedAdminFiltredActions[itemIndex],
        link: type === 'link' ? event.target.value : updatedAdminFiltredActions[itemIndex].link,
      };
    } else {
      updatedAdminFiltredActions.push({
        ...(isEntityScoop
          ? {
              isEntityScoop: true,
              versionProductId: selectedEntity?._id,
            }
          : {
              relatedProductId,
              versionProductId,
              versionProductName: getVersionName(selectedEntity._id, relatedProductId, versionProductId),
            }),
        ...action,
        link: type === 'link' ? event.target.value : null,
      });
    }
    dispatch(setLoading(true));

    dispatch(updateActionPlan({ accountId: selectedAccount._id, entityId: selectedEntity._id, actions: updatedAdminFiltredActions }));

    dispatch(setLoading(false));
  };

  const getCheckedActions = action => {
    const checkedAction = checkedActions.find(checkedAction => checkedAction.id === action.id);

    return checkedAction ? true : false;
  };

  const handleToDeleteActions = (productId, actionId, versionProductId) => {
    setToDeleteActions(prevState => {
      const itemIndex = prevState.findIndex(
        item => item.id === actionId && item.relatedProductId === productId && item.versionProductId === versionProductId
      );
      if (itemIndex !== -1) {
        // Item already exists, remove it
        const updatedState = prevState.slice();
        updatedState.splice(itemIndex, 1);
        return updatedState;
      } else {
        // Item doesn't exist, add it to the array
        return [
          ...prevState,
          {
            id: actionId,
            relatedProductId: productId,
            versionProductId,
          },
        ];
      }
    });
  };

  const handleShowActionDetail = (task, ItemID) => {
    if (showDrawer && selectedAction) {
      setSelectedAction(null);
      setEditedAction(null);
    } else {
      setSelectedAction(task);
      setEditedAction(task);
      setSelectedDrawerItemID(ItemID);
    }
    setShowDrawer(prevState => !prevState);
  };

  const handleUpdateAuthorizedActions = upgrade => {
    dispatch(setLoading(true));
    const actions = checkedActions.map(elt => {
      const contentfulActionDetails = userActions.find(action => action.id === elt.id);
      const action = { ...contentfulActionDetails, ...elt };
      const propertiesToDelete = ['responsible', 'sentMails', 'createdAt'];
      for (const property of propertiesToDelete) {
        delete action?.[property];
      }
      if (action.category !== 'Audit') {
        delete action?.conditions;
      }
      return action;
    });

    dispatch(updateActionPlan({ accountId: selectedAccount._id, entityId: selectedEntity._id, actions, upgrade: upgrade }));

    dispatch(setLoading(false));
  };

  const handleShareAction = action => {
    const shareId =
      'entity/' + selectedEntity._id + '/product/' + action.relatedProductId + '/action/' + action.id + '/version/' + action.versionProductId;
    dispatch(setCurrentEntity(selectedEntity));
    dispatch(
      setShareModal({
        relatedProductId: action.relatedProductId,
        type: 'actions',
        id: shareId,
        label: action.title,
      })
    );
    dispatch(setModal({ show: true, type: MODAL_TYPES.SHARE }));
  };

  const handleDeleteArchivedActions = () => {
    dispatch(setLoading(true));
    setFilters(prevState => ({ ...prevState, archived: !prevState['archived'] }));
    dispatch(updateActionPlan({ accountId: selectedAccount._id, entityId: selectedEntity._id, actions: toDeleteActions, mode: 'delete' }));
    dispatch(setLoading(false));
  };

  //
  // Custom Action handlers
  //

  const handleCustomDataChange = (e: ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = e.target;
    setCustomAction(prevState => {
      let updatedState;

      if (name === 'versionProductId') {
        if (value === selectedEntity._id) {
          updatedState = {
            ...prevState,
            [name]: value,
            isEntityScoop: true,
          };
          if (updatedState.relatedProductId) delete updatedState.relatedProductId;
        } else {
          const actionProductDetails = getProductsOptions()?.find(elt => elt.value === value);
          if (name === 'versionProductId' && actionProductDetails) {
            const { product, label, value } = actionProductDetails;
            updatedState = {
              ...prevState,
              relatedProductId: product,
              versionProductName: label,
              versionProductId: value,
            };
          }
        }
      } else {
        updatedState = {
          ...prevState,
          [name]: value,
        };
      }

      return {
        ...updatedState,
        isReady: checkCustomActionFields(updatedState),
      };
    });
  };

  const checkCustomActionFields = object => {
    const fieldsToCheck = ['title', 'description', 'complexity', 'category', 'urgency'];
    if (object.isEntityScoop) {
      fieldsToCheck.push('versionProductId');
    } else {
      fieldsToCheck.push('relatedProductId', 'versionProductId');
    }
    return fieldsToCheck.every(field => object[field] !== '' && object[field] !== undefined && object[field] !== null);
  };

  const handleCreateCustomAction = () => {
    dispatch(setLoading(true));
    delete customAction.isReady;
    dispatch(createCustomAction({ accountId: selectedAccount._id, entityId: selectedEntity._id, action: customAction }));
    dispatch(setLoading(false));
    setShowCustomActionModal(prevState => !prevState);
    setCustomAction({
      title: '',
      relatedProductId: '',
      description: '',
      complexity: '',
      urgency: '',
      conditions: [],
      category: '',
      custom: true,
      isReady: false,
    });
  };

  const getProductsOptions = () => {
    const products = [];
    const productsList = getAuthorizedProducts()
      .filter(product => product.versioning.enabled)
      .map(product => product.id);
    selectedAccount.entities
      .find(entity => entity._id === selectedEntity?._id)
      ?.responses?.forEach(response => {
        return (
          productsList.includes(response.surveyId) &&
          response?.versions?.forEach(version =>
            products.push({
              label: version?.versionName,
              value: version?._id,
              product: response.surveyId,
            })
          )
        );
      });
    return products;
  };

  //
  // Utils
  //

  const filteredData = data => {
    return (
      data
        .filter((item: ActionProps) => {
          switch (view) {
            case 'actions':
              return !['Demande', 'Audit'].includes(item.category);
            case 'requests':
              return item.category === 'Demande';
            case 'audits':
              return item.category === 'Audit';
            default:
              return true; // Include all items if view is not recognized
          }
        })
        // Apply search filter
        .filter((item: ActionProps) => {
          return compareQueryToActionName(item.title, query);
        })
        .filter((item: ActionProps) => {
          const { urgency, status, category, product, complexity, archived } = filters;
          // Apply urgency filter
          if (urgency.length > 0 && !urgency.some(option => option.value === item.urgency)) {
            return false;
          }
          // Apply status filter
          if (status.length > 0 && !status.some(option => option.value === item.status)) {
            return false;
          }
          // Apply category filter
          if (category.length > 0 && !category.some(option => option.value === item.category)) {
            return false;
          }
          // Apply product filter
          if (
            product.length > 0 &&
            !product.some(option => {
              if (item.isEntityScoop) {
                return option.value === item.versionProductId;
              } else {
                if (item.versionProductId) {
                  return option.value === item.versionProductId;
                }
                if (item.versionProductName && !item.versionProductId) {
                  const versionId = getVersionIdByName(selectedEntity?._id, item.relatedProductId, item.versionProductName, admin);
                  return option.value === versionId;
                }
              }
              return false;
            })
          ) {
            return false;
          }
          // Apply complexity filter
          if (complexity.length > 0 && !complexity.some(option => option.value === item.complexity)) {
            return false;
          }
          // Apply archive filter
          if ((archived && !item.isArchived) || (!archived && item.isArchived)) {
            return false;
          }
          return true;
        })
    );
  };

  const getActionLink = (action: ActionProps) => {
    return (
      checkedActions?.find(checkedAction => {
        if (checkedAction.isEntityScoop) {
          return checkedAction.id === action.id && checkedAction.versionProductId === action.versionProductId;
        } else {
          return (
            checkedAction.id === action.id &&
            checkedAction.versionProductId === action.versionProductId &&
            checkedAction.relatedProductId === action.relatedProductId
          );
        }
      })?.link || action?.link
    );
  };

  const handleDuplicateAction = (action: ActionProps) => {
    const { title, description, complexity, urgency, category, explanation, precision, conditions, draft } = action;
    setCustomAction({
      title,
      description,
      complexity,
      urgency,
      category,
      precision,
      explanation,
      conditions,
      custom: true,
      isReady: false,
      draft,
    });
    setShowCustomActionModal(true);
  };

  const handleSubcontractorInputModalClose = () => {
    setShowSubcontractorInputModal(false);
  };

  const handleUpdateSubContractorContact = async () => {
    dispatch(
      updateActionSubContractorInfo({
        accountId: activeAccount._id,
        entityId: selectedEntity._id,
        actionId: selectedAction.id,
        relatedProductId: selectedAction.relatedProductId,
        versionProductId: selectedAction.versionProductId,
        subContractorEmail: subContractorContact,
        subContractorName: selectedAction?.subContractor?.name,
        ...(!selectedAction?.dueDate && { dueDate: dayjs().add(9, 'week').format('DD-MM-YYYY') }),
      })
    );
    setSelectedAction(prevState => ({
      ...prevState,
      subContractor: { contactEmail: subContractorContact },
    }));
    setSubContractorContact('');
    setShowSubcontractorInputModal(false);
    setShowDrawer(false);
  };

  //
  // Create Client Data and consider the case if there is some shared actions
  //
  const clientData = data => {
    if (
      currentUser.role !== 'guest' ||
      currentUser.sharedItems.actions.find(action => action.entityId === selectedEntity._id && action.shareId === `entity-${action.entityId}`)
    ) {
      return filteredData(data);
    } else {
      return filteredData(
        data.filter(elt =>
          currentUser.sharedItems.actions.some(
            action =>
              action.entityId === selectedEntity._id &&
              action.actionId === elt.id &&
              action.relatedProductId === elt.relatedProductId &&
              action.versionProductId === elt.versionProductId
          )
        )
      );
    }
  };

  const isAnyNewComment = action => {
    if (Array.isArray(action.comment) && action.comment.length > 0) {
      for (const comment of action.comment) {
        const currentUserSeenIndex = comment.seenComment.findIndex(seen => seen.userID === currentUser._id);
        if (currentUserSeenIndex === -1) {
          return true;
        }
      }
    }
    return false;
  };

  //
  // Client Side Table Data
  //
  const clientTableData = () => {
    return (
      <>
        {clientSideActionsDataCount !== 0 ? (
          <DataTableContainer>
            <div className='container-fluid data-table'>
              {clientData(entityActions)
                .filter((action: ActionProps) => action.active)
                .sort(compareByUrgency)
                .map((action: ActionProps, index: number) => {
                  return (
                    compareQueryToActionName(action.title, query) && (
                      <div key={index}>
                        <RowContainer
                          className='row'
                          onClick={e => {
                            e.preventDefault();
                            handleShowActionDetail(action, 'detail');
                          }}>
                          {/* Action Title */}
                          <div className={'col-2'}>
                            {action.category === 'Audit'
                              ? action.custom
                                ? action?.title
                                : `Auditer : ${action?.subContractor?.name}`
                              : action?.title}
                          </div>

                          {/* Product Title */}
                          <div className={'col-1'}>{action.isEntityScoop ? selectedEntity.name : action.versionProductName}</div>

                          {/* Status */}
                          <Div className={'col-1'} center bolder>
                            <ActionStatus status={action.status} />
                          </Div>

                          {/* Date d'échéance  */}
                          <Div className={'col-1'} center>
                            {action.dueDate ? dayjs(action.dueDate, 'DD-MM-YYYY').format('DD-MM-YYYY').toString() : '-- / -- / ----'}
                          </Div>

                          {/* Urgency */}
                          <Div className='col-1' center>
                            <CustomChip label={actionsUrgencyComplexity[action?.urgency]} urgency={action?.urgency} variant='outlined' />
                          </Div>

                          {/* Complexity */}
                          <Div className='col-1' center>
                            <CustomChip label={actionsUrgencyComplexity[action?.complexity]} urgency={action?.complexity} variant='outlined' />
                          </Div>

                          {/* Category */}
                          <Div className='col-1' center>
                            <CustomChip label={action?.category} size='small' />
                          </Div>

                          {/* Task Responsable */}
                          <div className='col-1-5'>
                            <AvatarContainer max={4}>
                              {action?.responsible?.map((responsible, index) => (
                                <Avatar key={index} {...stringAvatar(getResponsibleName(responsible?.email).toUpperCase())} />
                              ))}
                            </AvatarContainer>
                          </div>

                          {/* Actions */}
                          <div className='col-1'>
                            <CustomStack direction='row' spacing={2}>
                              {/* Share */}
                              <div ref={attributeRef} style={{ cursor: 'pointer' }}>
                                <RiShareForwardLine
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleShareAction(action);
                                  }}
                                />
                                <Tippy content='Attribuer la tache' reference={attributeRef} placement='bottom' />
                              </div>

                              {/* Discussion */}

                              <div style={{ cursor: 'pointer' }}>
                                {isAnyNewComment(action) ? (
                                  <Infos>
                                    <ChatBubbleIcon
                                      active={Array.isArray(action.comment) && action.comment.length > 0}
                                      onClick={e => {
                                        e.stopPropagation();
                                        handleShowActionDetail(action, 'chat');
                                      }}
                                    />
                                  </Infos>
                                ) : (
                                  <ChatBubbleIcon
                                    active={Array.isArray(action.comment) && action.comment.length > 0}
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleShowActionDetail(action, 'chat');
                                    }}
                                  />
                                )}
                              </div>

                              {/* Access documents Link */}
                              {action.link && (
                                <div ref={documentRef} style={{ cursor: 'pointer' }} onClick={e => handleAttachmentClick(e, action.link)}>
                                  <CgAttachment />
                                  <Tippy content='Vos documents' reference={documentRef} placement='bottom' />
                                </div>
                              )}
                            </CustomStack>
                          </div>
                        </RowContainer>
                        {index < clientSideActionsDataCount - 1 && <Divider light />}
                      </div>
                    )
                  );
                })}
            </div>
          </DataTableContainer>
        ) : (
          <NoDataContainer>{noTableDataAvailable(view)}</NoDataContainer>
        )}
        {clientSideActionsDataCount !== 0 && <Divider />}
      </>
    );
  };

  const getItemIcon = (type, onClick) => {
    switch (type) {
      case 'docx':
        return <AiOutlineFileWord onClick={onClick} />;
      case 'xlsx':
        return <AiOutlineFileExcel onClick={onClick} />;
      case 'pdf':
        return <AiOutlineFilePdf onClick={onClick} />;
      default:
        return <AiOutlineFileDone onClick={onClick} />;
    }
  };

  const handleSelectActions = action => {
    const actionIndex = checkedActions.findIndex(checkedAction => checkedAction.id === action.id);

    if (actionIndex !== -1) {
      setCheckedActions(prevCheckedActions => prevCheckedActions.filter(item => item.id !== action.id));
    } else {
      const updatedAction = {
        ...(action.isEntityScoop
          ? {
              isEntityScoop: true,
              versionProductId: selectedEntity?._id,
            }
          : {
              relatedProductId,
              versionProductId,
              versionProductName: getVersionName(selectedEntity._id, relatedProductId, versionProductId),
            }),
        ...action,
      };

      setCheckedActions(prevCheckedActions => [...prevCheckedActions, updatedAction]);
    }
  };

  //
  // Admin Side Table Data
  //
  const adminTableData = () => {
    return (
      <>
        <DataTableContainer admin>
          {adminFiltredActions.length > 0 ? (
            <div className='container-fluid data-table'>
              {adminFiltredActions.sort(compareByUrgency)?.map((action: ActionProps, index: number) => {
                return (
                  <div key={index}>
                    <AdminRowContainer
                      className='row'
                      backlog={view === 'backlog'}
                      onClick={e => {
                        e.preventDefault();
                        handleShowActionDetail(action, 'detail');
                      }}>
                      {/* Admin validation */}
                      <Div className='col-0-5' center>
                        {filters.archived ? (
                          <IconButton
                            onClick={() => handleToDeleteActions(action.relatedProductId, action.id, action.versionProductId)}
                            size='large'
                            sx={{ fontSize: 20, color: 'red' }}>
                            <RiDeleteBin5Line />
                          </IconButton>
                        ) : (
                          <Checkbox
                            checked={getCheckedActions(action)}
                            onClick={e => {
                              e.stopPropagation();
                            }}
                            onChange={e => handleSelectActions(action)}
                          />
                        )}
                      </Div>

                      {/* Action Title */}
                      <div className={'col-1-5'}>
                        {action.category === 'Audit' ? (action.custom ? action?.title : `Auditer : ${action?.subContractor?.name}`) : action?.title}
                      </div>

                      {/* Produit / Entity */}
                      <div className={'col-1'}>
                        {action.isEntityScoop
                          ? selectedEntity.name
                          : action.versionProductName || getAuthorizedProducts().find(product => product.id === action)}
                      </div>

                      {/* Status */}
                      <Div className={'col-1'} center>
                        <ActionStatus status={action.status} />
                      </Div>

                      {/* Date d'échéance  */}
                      <Div className={'col-1'} center>
                        {action.dueDate ? dayjs(action.dueDate, 'DD-MM-YYYY').format('DD-MM-YYYY').toString() : '-- / -- / ----'}
                      </Div>

                      {/* Urgency */}
                      <Div className='col-1' center>
                        <CustomChip label={actionsUrgencyComplexity[action?.urgency]} urgency={action?.urgency} variant='outlined' />
                      </Div>

                      {/* Complexity */}
                      <Div className='col-1' center>
                        <CustomChip label={actionsUrgencyComplexity[action?.complexity]} urgency={action?.complexity} variant='outlined' />
                      </Div>

                      {/* Category */}
                      <Div className='col-1' center>
                        <CustomChip label={action?.category} />
                      </Div>

                      {/* Action Responsable */}
                      <Div className='col-1'>
                        <AvatarContainer max={3}>
                          {action?.responsible?.map((responsible, index) => (
                            <Avatar key={index} {...stringAvatar(getResponsibleName(responsible?.email).toUpperCase())} />
                          ))}
                        </AvatarContainer>
                      </Div>

                      {/* Action document link */}
                      <div className='col-1'>
                        <LinkField type={getActionLink(action)?.split('.')?.pop()}>
                          {(getActionLink(action) === '' || getActionLink(action) === undefined || getActionLink(action) === null) && (
                            <div className='col-0-5'>
                              <AiOutlineFileAdd className={'add'} onClick={e => handleFocusLinkInput(e, action, 'table')} />
                              <ErasePlaceholder />
                            </div>
                          )}
                          {getActionLink(action) !== '' && getActionLink(action) !== null && getActionLink(action) !== undefined && (
                            <>
                              <div>
                                {getItemIcon(getActionLink(action)?.split('.')?.pop(), e => {
                                  action.link && handleFocusLinkInput(e, action, 'table');
                                })}
                              </div>

                              <div>
                                {!action.active && action.link && (
                                  <PiXCircleBold
                                    className={'erase'}
                                    color='red'
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleCheckedActions({ target: { checked: true, value: '' } }, action, 'link');
                                    }}
                                  />
                                )}
                              </div>
                            </>
                          )}
                        </LinkField>
                      </div>
                      {/* Duplicate */}
                      {!action.active && (
                        <Div className='col-0-5' center>
                          <BiDuplicate
                            size={24}
                            onClick={e => {
                              e.stopPropagation();
                              handleDuplicateAction(action);
                            }}
                          />
                        </Div>
                      )}

                      <div className='col-0-5' style={{ display: 'flex' }}>
                        {/* Discussion */}
                        {action.active && (
                          <>
                            {isAnyNewComment(action) ? (
                              <Infos>
                                <ChatBubbleIcon
                                  active={Array.isArray(action.comment) && action.comment.length > 0}
                                  size={24}
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleShowActionDetail(action, 'chat');
                                  }}
                                />
                              </Infos>
                            ) : (
                              <ChatBubbleIcon
                                active={Array.isArray(action.comment) && action.comment.length > 0}
                                size={24}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleShowActionDetail(action, 'chat');
                                }}
                              />
                            )}
                          </>
                        )}
                      </div>
                    </AdminRowContainer>
                    {index !== adminFiltredActions.length - 1 && <Divider light />}
                  </div>
                );
              })}
            </div>
          ) : (
            <h2 style={{ margin: '15% auto 0 auto', color: 'black' }}> Aucune action n'est disponible pour le moment</h2>
          )}
        </DataTableContainer>
        {adminFiltredActions.length > 1 && <Divider />}
      </>
    );
  };

  //
  // handle focus on link input
  //
  const handleFocusLinkInput = (e, action, context = undefined) => {
    if (!action.active) {
      e.stopPropagation();
      setShowAvailableDocuments({ action, show: true, context });
    }
  };

  const handleFileChoice = fileKey => {
    if (showAvailableDocuments.context === 'table') {
      handleCheckedActions(
        { target: { checked: true, value: fileKey } },
        showAvailableDocuments.action,
        'link',
        showAvailableDocuments.action.isEntityScoop
      );
    } else {
      handleCustomDataChange({ target: { name: 'link', value: fileKey } });
    }
    setShowAvailableDocuments({ action: undefined, show: false, context: undefined });
  };

  const handleAttachmentClick = async (e, link) => {
    e?.stopPropagation();
    let signedLink = await getSignedUrlByKey(link);
    window.open(signedLink.link, '_blank');
  };

  const handleCustomActionModalClose = e => {
    setCustomAction(customActionInitialState);
    setShowCustomActionModal(false);
  };

  const noTableDataAvailable = view => {
    switch (view) {
      case 'actions':
        return "Aucune actions n'est encore disponible pour votre entité";
      case 'requests':
        return "Aucune Demande de droits n'est encore disponible pour votre entité";
      case 'audits':
        return "Aucune audits n'est encore disponible pour votre entité";
      default:
        return "Progression du plan d'action";
    }
  };

  return (
    <>
      {selectedEntity && admin && (
        <HeaderActions>
          {view === 'backlog' ? (
            <>
              <ActionContainer>
                <Button
                  onClick={e => {
                    e.preventDefault();
                    setShowCustomActionModal(true);
                  }}
                  label={'Ajouter une action personnalisée'}
                />
              </ActionContainer>
              <ActionContainer>
                <Button onClick={() => handleUpdateAuthorizedActions('upgrade')} label={'Transférer vers actions préparées'} customColor='#36B894' />
              </ActionContainer>
            </>
          ) : view === 'actionsPrepared' ? (
            <>
              <ActionContainer>
                <Button onClick={() => handleUpdateAuthorizedActions('downgrade')} label={'Rétrograder'} customColor='#36B894' />
              </ActionContainer>
              <ActionContainer>
                <Button onClick={() => handleUpdateAuthorizedActions('upgrade')} label={'Activer'} customColor='#36B894' />
              </ActionContainer>
            </>
          ) : (
            <>
              {filters.archived ? (
                <ActionContainer>
                  <Button onClick={handleDeleteArchivedActions} label={'Supprimer les actions archivées'} type='danger' />
                </ActionContainer>
              ) : (
                <ActionContainer>
                  <Button onClick={() => handleUpdateAuthorizedActions('downgrade')} label={'Rétrograder'} customColor='#36B894' />
                </ActionContainer>
              )}
            </>
          )}
        </HeaderActions>
      )}
      <ActionsHeader
        query={query}
        setQuery={setQuery}
        filters={filters}
        setFilters={setFilters}
        admin={admin}
        selectedEntity={selectedEntity}
        setSelectedEntity={setSelectedEntity}
        setLoading={setLoading}
        view={view}
      />
      <DataTableContainer>
        <div className='container-fluid data-table font-weight-bold'>
          <div className='row'>
            {admin && (
              <Div className='col-0-5' bolder center>
                {filters.archived ? (
                  ''
                ) : (
                  <Checkbox
                    checked={selectAllActions}
                    onClick={e => {
                      e.stopPropagation();
                    }}
                    onChange={e => setSelectAllActions(!selectAllActions)}
                  />
                )}
              </Div>
            )}
            <Div className={admin ? 'col-1-5' : 'col-2'} bolder>
              <span>Action</span>
            </Div>
            <Div className='col-1' bolder>
              Produit / Entité
            </Div>
            <Div className='col-1' center bolder>
              Statut
            </Div>
            <Div className='col-1' center bolder>
              Date d'échéance
            </Div>
            <Div className='col-1' center bolder>
              Criticité
            </Div>
            <Div className={admin ? 'col-1' : 'col-1'} center bolder>
              Complexité
            </Div>
            <Div className={admin ? 'col-1' : 'col-1'} center bolder>
              Catégorie
            </Div>
            <Div className={admin ? 'col-1' : 'col-1-5'} bolder>
              {!admin ? 'Responsable de la tâche' : 'Responsable'}
            </Div>
            {!admin && <Div className='col-1' bolder></Div>}
            {admin && (
              <>
                <Div className='col-1' bolder>
                  Document
                </Div>
                {view !== 'actions' && (
                  <Div className='col-0-5' bolder>
                    Dupliquer
                  </Div>
                )}
              </>
            )}
          </div>
        </div>
        <Divider />
      </DataTableContainer>
      {isDataLoading ? loadingTable() : selectedEntity ? (!admin ? clientTableData() : adminTableData()) : null}
      {/* {selectedEntity && !admin && clientTableData()}
      {selectedEntity && admin && adminTableData()} */}

      {/* Action details Drawer */}
      <ActionDetails
        impersonated={impersonated}
        isAnyNewComment={isAnyNewComment}
        selectedAction={selectedAction}
        activeAccount={activeAccount}
        setSelectedAction={setSelectedAction}
        selectedEntity={selectedEntity}
        showDrawer={showDrawer}
        setShowDrawer={setShowDrawer}
        selectedDrawerItemID={selectedDrawerItemID}
        currentUser={currentUser}
        admin={admin}
        editedAction={editedAction}
        setShowSubcontractorInputModal={setShowSubcontractorInputModal}
      />
      {/* Custom Action Creation Modal */}
      {admin && showCustomActionModal && (
        <Modal show={showCustomActionModal} title={"Création d'une action personnalisé "} onClose={handleCustomActionModalClose}>
          <ModalContent>
            <TextField
              label='Titre '
              variant='outlined'
              name='title'
              type='search'
              fullWidth
              defaultValue={customAction?.title}
              onChange={handleCustomDataChange}
              required
            />
            <AdjustActions>
              <TextField
                label='Produit / Entité'
                variant='outlined'
                name='versionProductId'
                fullWidth
                select
                onChange={handleCustomDataChange}
                required>
                <MenuItem disabled>Produit</MenuItem>
                {getProductsOptions().map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
                <MenuItem disabled>Entité</MenuItem>
                <MenuItem key={'entity'} value={selectedEntity._id}>
                  {selectedEntity.name}
                </MenuItem>
              </TextField>
              <TextField
                label='Catégorie '
                variant='outlined'
                name='category'
                type='search'
                select
                fullWidth
                defaultValue={customAction?.category}
                onChange={handleCustomDataChange}
                required>
                {category.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </AdjustActions>

            <AdjustActions>
              <TextField
                label='Criticité '
                variant='outlined'
                name='urgency'
                select
                fullWidth
                defaultValue={customAction?.urgency}
                onChange={handleCustomDataChange}
                required>
                {urgencyComplexity.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label='Complexité '
                variant='outlined'
                name='complexity'
                select
                fullWidth
                defaultValue={customAction?.complexity}
                onChange={handleCustomDataChange}
                required>
                {urgencyComplexity.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </AdjustActions>

            <TextField
              // defaultValue={''}
              value={getFileNameFromKey(customAction?.link) ?? ''}
              label='Lien document '
              variant='outlined'
              name='link'
              type='search'
              fullWidth
              onFocus={e => handleFocusLinkInput(e, customAction)}
            />

            <TextField
              label='Description'
              variant='outlined'
              name='description'
              multiline
              rows={3}
              fullWidth
              defaultValue={customAction?.description}
              onChange={handleCustomDataChange}
              required
            />
            <TextField
              label="Explication d'intégration"
              variant='outlined'
              name='explanation'
              multiline
              rows={2}
              fullWidth
              defaultValue={customAction?.explanation}
              onChange={handleCustomDataChange}
            />
            <TextField
              label='Précision'
              variant='outlined'
              name='precision'
              multiline
              rows={2}
              fullWidth
              defaultValue={customAction?.precision}
              onChange={handleCustomDataChange}
            />
          </ModalContent>
          <ButtonsContainer>
            <Button disabled={!customAction?.isReady} onClick={handleCreateCustomAction} label={'Valider'} />
            <Button onClick={handleCustomActionModalClose} label={'Annuler'} type='light' />
          </ButtonsContainer>
        </Modal>
      )}
      {showAvailableDocuments?.show && (
        <Modal
          size={{ height: '100%' }}
          show={showAvailableDocuments?.show}
          title={'Documents disponibles'}
          onClose={() => setShowAvailableDocuments({ action: undefined, show: false, context: undefined })}>
          <FileExplorer
            embedded={true}
            selectedEntity={selectedEntity}
            customClick={handleFileChoice}
            selectedFileKey={showAvailableDocuments?.action?.link}
            admin
          />
        </Modal>
      )}
      {!admin && showDrawer && showSubcontractorInputModal && selectedAction.category === 'Audit' && (
        <Modal
          show={showSubcontractorInputModal}
          title={'Saisie le contact de votre prestataire'}
          onClose={handleSubcontractorInputModalClose}
          overAll>
          <BasicModalContainer>
            <InputComponent
              focus={true}
              placeholder={'Email de votre prestataire'}
              sharable={false}
              type={'text'}
              onChange={e => setSubContractorContact(e.target.value)}
              value={subContractorContact}
            />
            <ButtonContainer>
              <Button label={'Ajouter'} onClick={() => handleUpdateSubContractorContact()} disabled={!isEmailValid(subContractorContact)} />
              <Button type={'light'} onClick={() => setShowSubcontractorInputModal(false)} label={'Annuler'} />
            </ButtonContainer>
          </BasicModalContainer>
        </Modal>
      )}
    </>
  );
};

export default Table;
